import {useEffect, useRef} from 'react'
import {defineMessages} from 'react-intl'

import {useToastController} from '@vio/atlas/context/toastController'
import {Icon} from '@vio/atlas/Icon'
import {useNetworkStatus} from '@vio/core/src/_web/utils/network/hooks/useNetworkStatus'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'

const toastStyle = {width: 'fit-content', minWidth: 'auto'}

const messages = defineMessages({
  offline: {
    id: 'networkStatus.offline',
    defaultMessage:
      "You're offline. Connect to a network to continue your booking."
  },
  online: {
    id: 'networkStatus.online',
    defaultMessage: 'You are now online'
  }
})

export const NetworkStatusToast = () => {
  const isOnline = useNetworkStatus()
  const toast = useToastController()

  const prevIsOnlineRef = useRef<boolean | undefined>(undefined)
  const offlineToastIdRef = useRef<string | null>(null)

  useEffect(() => {
    const prevIsOnline = prevIsOnlineRef.current

    // Skip initial mount — only react to transitions
    if (prevIsOnline === undefined) {
      prevIsOnlineRef.current = isOnline
      return
    }

    // Online → Offline transition
    if (prevIsOnline && !isOnline) {
      offlineToastIdRef.current = toast.open(
        <FormattedMessageWrapper {...messages.offline} />,
        {
          autoClose: false,
          icon: <Icon name="WifiOff" />,
          style: toastStyle
        }
      )
    }

    // Offline → Online transition
    if (!prevIsOnline && isOnline) {
      // Close the persistent offline toast
      if (offlineToastIdRef.current) {
        toast.close(offlineToastIdRef.current)
        offlineToastIdRef.current = null
      }

      toast.open(<FormattedMessageWrapper {...messages.online} />, {
        icon: <Icon name="Wifi" />,
        style: toastStyle
      })
    }

    prevIsOnlineRef.current = isOnline

    return () => {
      if (offlineToastIdRef.current) {
        toast.close(offlineToastIdRef.current)
        offlineToastIdRef.current = null
      }
    }
  }, [isOnline, toast])

  return null
}
